import { useState, useEffect } from "react";
import { useParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import Checkbox from "../components/form/input/Checkbox";
import { tenantService, Tenant, TenantContact } from "../services/tenantService";
import { buildingService } from "../services/buildingService";
import { useBuilding } from "../context/BuildingContext";

type Tab = "contacts" | "settings";

export default function TenantDetail() {
  const { id, tenantId } = useParams<{ id: string; tenantId: string }>();
  const buildingId = id ? parseInt(id) : undefined;
  const tenantIdNum = tenantId ? parseInt(tenantId) : undefined;
  const { currentBuilding, selectBuilding } = useBuilding();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [contacts, setContacts] = useState<TenantContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("contacts");

  // Settings form state
  const [centralDeliveryEnabled, setCentralDeliveryEnabled] = useState(false);
  const [centralDeliveryLocation, setCentralDeliveryLocation] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Sync building context
  useEffect(() => {
    const syncBuilding = async () => {
      if (!buildingId) return;
      if (currentBuilding && currentBuilding.id === buildingId) return;
      try {
        const building = await buildingService.getBuilding(buildingId);
        selectBuilding(building);
      } catch (err) {
        console.error("Error syncing building:", err);
      }
    };
    syncBuilding();
  }, [buildingId, currentBuilding, selectBuilding]);

  // Fetch tenant and contacts
  useEffect(() => {
    const fetchData = async () => {
      if (!tenantIdNum) return;
      setIsLoading(true);
      setError(null);
      try {
        const [tenantData, contactsData] = await Promise.all([
          tenantService.getTenant(tenantIdNum),
          tenantService.getTenantContacts(tenantIdNum),
        ]);
        setTenant(tenantData);
        setContacts(contactsData);
        setCentralDeliveryEnabled(tenantData.central_delivery_enabled ?? false);
        setCentralDeliveryLocation(tenantData.central_delivery_location ?? "");
      } catch (err) {
        console.error("Error fetching tenant:", err);
        setError("Failed to load tenant. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [tenantIdNum]);

  const handleSaveSettings = async () => {
    if (!tenant) return;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const updated = await tenantService.updateTenant(tenant.id, {
        name: tenant.name,
        building_id: tenant.building_id,
        central_delivery_enabled: centralDeliveryEnabled,
        central_delivery_location: centralDeliveryEnabled
          ? centralDeliveryLocation
          : null,
      });
      setTenant(updated);
      setSaveMessage("Settings saved successfully.");
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save settings";
      setSaveMessage(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!buildingId || !tenantIdNum) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-gray-500 dark:text-gray-400">Invalid tenant</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-red-500">{error || "Tenant not found"}</p>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title={`${tenant.name} | Tenants | SBM Package Admin`}
        description={`Manage tenant ${tenant.name}`}
      />

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
            {tenant.name}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {currentBuilding?.name}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="-mb-px flex gap-6">
            <button
              onClick={() => setActiveTab("contacts")}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === "contacts"
                  ? "border-brand-500 text-brand-500"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              Contacts
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === "settings"
                  ? "border-brand-500 text-brand-500"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
              }`}
            >
              Settings
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "contacts" && (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 p-5 dark:border-gray-800">
              <h3 className="font-semibold text-gray-800 dark:text-white/90">
                Contacts ({contacts.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800">
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Name
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Floor
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Phone
                    </th>
                    <th className="px-5 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {contacts.map((contact) => (
                    <tr
                      key={contact.id}
                      className="hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-5 py-4 font-medium text-gray-800 dark:text-white/90">
                        {contact.first_name} {contact.last_name}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {contact.email || "—"}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {contact.floor || "—"}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {contact.phone || "—"}
                      </td>
                      <td className="px-5 py-4 text-center">
                        {contact.status === 1 ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            Archived
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {contacts.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center">
                        <p className="text-gray-500 dark:text-gray-400">
                          No contacts found for this tenant
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 p-5 dark:border-gray-800">
              <h3 className="font-semibold text-gray-800 dark:text-white/90">
                Delivery Settings
              </h3>
            </div>
            <div className="p-5 space-y-6">
              <Checkbox
                label="Central Delivery Point"
                checked={centralDeliveryEnabled}
                onChange={setCentralDeliveryEnabled}
              />

              {centralDeliveryEnabled && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Delivery Location
                  </label>
                  <input
                    type="text"
                    value={centralDeliveryLocation}
                    onChange={(e) => setCentralDeliveryLocation(e.target.value)}
                    placeholder="e.g. Mailroom - 1st Floor"
                    className="w-full max-w-md rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                  <p className="mt-1.5 text-xs text-gray-500">
                    This location will be shown to floor reps when delivering
                    packages for this tenant.
                  </p>
                </div>
              )}

              {saveMessage && (
                <p
                  className={`text-sm ${
                    saveMessage.includes("success")
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {saveMessage}
                </p>
              )}

              <button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
